import { ArrowLeftIcon, ArrowPathIcon, ListBulletIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { type InputType, SettingKey } from 'config'
import type { ILoanGlobalSettings } from 'pages/Admin/AdminTools/Configuration/GlobalSettings'
import {
  ClosingCostPlaceholderType,
  IClosingCostSetup,
} from 'pages/Admin/AdminTools/Configuration/HUD1/ClosingCostSetup/type'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createLoanTermSheetTemp,
  getAdminConfig,
  getAllLoanTermSheetDocuments,
  getClosingCostSetups,
  removeLoanTermSheetField,
  updateLoanTermSheetTemp,
} from 'services'
import { Button, Tooltip } from 'stories/components'
import { confirm, InputValidate, toCamelCase } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { AddTermSheetField } from './AddTermSheetField'
import {
  DEFAULT_DOCUMENT_TITLE,
  DEFAULT_SHEET_TYPE,
  ILoanTermSheetTemp,
  TermSheetPlaceholders,
  TermSheetTemplateSourceOptions,
  termSheetTypeInputs,
} from './constants'
import { validatePlaceholders } from './logic'
import { PickPlaceholdersUp } from './PickPlaceholdersUp'
import { SyncTermSheetField } from './SyncTermSheetField'

const termSheetTitles: Record<string, string> = {
  headerText: 'Header Text',
  exhibitA: 'Exhibit A',
}

interface IProps {
  defaults: ILoanTermSheetTemp | null
  isDuplicated?: boolean
  lastUpdatedAt: number
  allTemplates: ILoanTermSheetTemp[]
  onBack: () => void
  onComplete: () => void
}

export const LoanTermSheetDetails = (props: IProps) => {
  const { defaults, isDuplicated = false, allTemplates, onBack, onComplete } = props

  const isNew = !defaults

  const [isLoading, setIsLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(termSheetTypeInputs())
  const [data, setData] = useState<ILoanTermSheetTemp | undefined>(defaults || undefined)
  const [origin, setOrigin] = useState<ILoanTermSheetTemp | undefined>(defaults || undefined)
  const [edit, setEdit] = useState(false)
  const [add, setAdd] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [titleErrors, setTitleErrors] = useState<Record<number, string>>({})
  const [modal, setModal] = useState('')
  const [syncItem, setSyncItem] = useState({})
  const [globalSetting, setGlobalSetting] = useState<ILoanGlobalSettings>()
  const [closingCostSetups, setClosingCostSetups] = useState<IClosingCostSetup[]>([])
  const [cursorPos, setCursorPos] = useState(0)
  const [focusField, setFocusField] = useState<Record<string, any>>({ type: '', index: 0 })
  const activeInputRef = useRef(null)
  const [docgenTemplates, setDocgenTemplates] = useState<{ id: number; name: string; placeholders: string[] }[]>([])

  const templateSource = data?.source || 'current'
  const isDocgenSource = templateSource === 'docgen'

  const commitmentFeeIsRequired = useMemo(() => {
    if (!globalSetting) return true

    const productType = data?.productType || inputs.productType?.value
    const transactionType = data?.transactionType || inputs.transactionType?.value

    if (!productType || !transactionType) return true

    return !!globalSetting.commitmentFeeRequiredTypes.find(
      (item) => item.productType === productType && item.transactionType === transactionType,
    )
  }, [globalSetting, data, inputs.productType, inputs.transactionType])

  const dueDilligenceIsRequired = useMemo(() => {
    if (!globalSetting) return true

    const productType = data?.productType || inputs.productType?.value
    const transactionType = data?.transactionType || inputs.transactionType?.value

    if (!productType || !transactionType) return true

    return !!globalSetting.dueDilligenceRequiredTypes.find(
      (item) => item.productType === productType && item.transactionType === transactionType,
    )
  }, [globalSetting, data, inputs.productType, inputs.transactionType])

  const channelError = useMemo(() => {
    const productType = inputs.productType?.value || data?.productType
    const transactionType = inputs.transactionType?.value || data?.transactionType
    const sheetType = inputs.sheetType?.value || data?.sheetType || DEFAULT_SHEET_TYPE
    const currentTypes: Record<string, any> = (inputs.types?.value || {}) as Record<string, any>

    if (!productType || !transactionType) return ''

    // When duplicating or creating, exclude nothing — all existing templates are potential conflicts.
    // When editing, exclude the current template itself.
    const excludeId = !isNew && !isDuplicated ? defaults?.id : undefined
    const takenChannels: string[] = []

    allTemplates
      .filter(
        (t) =>
          t.productType === productType &&
          t.transactionType === transactionType &&
          (t.sheetType || DEFAULT_SHEET_TYPE) === sheetType &&
          t.id !== excludeId,
      )
      .forEach((t) => {
        Object.keys(t.types || {}).forEach((ch) => {
          if (t.types[ch]) takenChannels.push(ch)
        })
      })

    const conflicting = Object.keys(currentTypes).filter((ch) => currentTypes[ch] && takenChannels.includes(ch))

    if (conflicting.length === 0) return ''

    const channelNames: Record<string, string> = {
      '0': 'Wholesale',
      '1': 'Correspondent',
      '2': 'Retail',
      '3': 'Table Funder',
      '4': 'Brokered',
    }
    const names = conflicting.map((ch) => channelNames[ch] || ch).join(', ')
    return `${names} already used in another ${productType} / ${transactionType} template`
  }, [
    data,
    inputs.productType,
    inputs.transactionType,
    inputs.types,
    inputs.sheetType,
    allTemplates,
    defaults,
    isNew,
    isDuplicated,
  ])

  useEffect(() => {
    setIsLoading(true)
    getAdminConfig(SettingKey.GLOBAL_SETTINGS)
      .then((content) => setGlobalSetting(content))
      .finally(() => setIsLoading(false))

    getClosingCostSetups()
      .then(({ data }) => {
        setClosingCostSetups(data)
      })
      .finally(() => {})

    getAllLoanTermSheetDocuments()
      .then((res: any) => {
        setDocgenTemplates(res?.documents || [])
        if (!(res?.documents || []).length) {
          toast('No active Docgen Loan Term Sheet templates found. Upload them in Docgen → Loan Applications → Loan Term Sheet.', {
            type: 'info',
          })
        }
      })
      .catch((e: any) => {
        setDocgenTemplates([])
        toast(e?.message || 'Failed to load Docgen term sheet templates', { type: 'error' })
      })

    if (defaults) {
      const newInputs = cloneDeep(termSheetTypeInputs())
      newInputs.productType.value = defaults.productType
      newInputs.transactionType.value = defaults.transactionType
      newInputs.sheetType.value = defaults.sheetType || DEFAULT_SHEET_TYPE
      if (!isDuplicated) {
        newInputs.types.value = defaults.types
      }
      setInputs(newInputs)
      setData({
        ...defaults,
        sheetType: defaults.sheetType || DEFAULT_SHEET_TYPE,
        documentTitle: defaults.documentTitle || DEFAULT_DOCUMENT_TITLE,
        source: defaults.source || 'current',
        docgenTemplateName: defaults.docgenTemplateName || null,
      })
      setOrigin({
        ...defaults,
        sheetType: defaults.sheetType || DEFAULT_SHEET_TYPE,
        documentTitle: defaults.documentTitle || DEFAULT_DOCUMENT_TITLE,
        source: defaults.source || 'current',
        docgenTemplateName: defaults.docgenTemplateName || null,
      })
    }

    setEdit(false)
    setErrors({})
    setTitleErrors({})
  }, [])

  const onChangeSource = async (key: string, value: any) => {
    if (!data) return
    const newData = cloneDeep(data)
    if (key === 'source') {
      newData.source = value
      if (value === 'current') newData.docgenTemplateName = null
    } else if (key === 'docgenTemplateName') {
      newData.docgenTemplateName = value || null
    }
    setData(newData)
    if (isDuplicated) return
    if (channelError) return
    if (key === 'source' && value === 'docgen' && !newData.docgenTemplateName) return
    setIsLoading(true)
    await updateLoanTermSheetTemp(newData.id, newData)
    setOrigin(cloneDeep(newData))
    setIsLoading(false)
  }

  const closingCostPlaceholders = useMemo(() => {
    const placeholders: string[] = []
    closingCostSetups
      .filter((item: any) => item.addTermSheet && item.lineName)
      .map((item) => {
        const rtl = []
        if (item.placeholderType !== ClosingCostPlaceholderType.Percentage)
          rtl.push(`${toCamelCase(item.lineName)}Amount`)
        if (item.placeholderType !== ClosingCostPlaceholderType.Amount)
          rtl.push(`${toCamelCase(item.lineName)}Percentage`)
        placeholders.push(...rtl)
      })
    return placeholders
  }, [closingCostSetups])

  const onChangeType = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    setInputs(newInputs)

    if (data) {
      const newData = cloneDeep(data)
      ;(newData as any)[key] = value
      setData(newData)
    }
  }

  const onBlurType = async () => {
    if (!data) return
    // Don't auto-save when duplicating - user needs to click "Save as Duplicate" first
    if (isDuplicated) return
    // Don't auto-save when channel conflict exists
    if (channelError) return

    setIsLoading(true)
    await updateLoanTermSheetTemp(data.id, data)
    setOrigin(cloneDeep(data))
    setIsLoading(false)
  }

  const onChangeHideExhibitA = useCallback(
    async (_key: string, value: boolean) => {
      if (!data) return
      const newData = cloneDeep(data)
      newData.hideExhibitA = value
      setData(newData)
      if (isDuplicated) return
      if (channelError) return
      setIsLoading(true)
      await updateLoanTermSheetTemp(newData.id, newData)
      setOrigin(cloneDeep(newData))
      setIsLoading(false)
    },
    [data, isDuplicated, channelError],
  )

  const onChangeHeaderExhibit = (key: 'headerText' | 'exhibitA' | 'documentTitle', value: string) => {
    if (!data) return
    const newData = cloneDeep(data)
    const newErrors = cloneDeep(errors)

    delete newErrors[key]

    if (key !== 'documentTitle' && !value) return

    newData[key] = value
    setErrors(newErrors)
    setData(newData)
    setEdit(true)
  }

  const onChangeFields = (index: number, value: string) => {
    if (!data) return
    const newData = cloneDeep(data)
    const newErrors = cloneDeep(errors)

    if (!value) return

    delete newErrors[newData.fields[index].title]
    newData.fields[index].content = value
    setData(newData)
    setErrors(newErrors)
    setEdit(true)
  }

  const onChangeTitle = (index: number, value: string) => {
    if (!data) return
    const newData = cloneDeep(data)
    const newTitleErrors = cloneDeep(titleErrors)
    delete newTitleErrors[index]

    if (!value) return

    newData.fields[index].title = value
    setData(newData)
    setTitleErrors(newTitleErrors)
    setEdit(true)
  }

  const onBlurTitle = async (index: number) => {
    if (!data) return
    if (!origin) return
    if (!edit) return
    // Don't auto-save when duplicating - user needs to click "Save as Duplicate" first
    if (isDuplicated) return
    const newData = cloneDeep(data)

    const value = data.fields[index].title.trim()
    newData.fields[index].title = value
    const oldValue = origin.fields[index]?.title

    if (value === oldValue) return

    const existIndex = data.fields.findIndex((item) => item.title === value)
    if (existIndex !== -1 && existIndex !== index) {
      const newTitleErrors = cloneDeep(titleErrors)
      newTitleErrors[index] = `This title already exists`
      setTitleErrors(newTitleErrors)
      setData(newData)
      toast(`Please input another title`, { type: 'error' })
      return
    }

    const content = (
      <div className="text-gray-400 mb-4 text-[16px]">
        Are you sure to change this title?
        <br />
        <span className="text-gray-800 text-base">
          {oldValue} {`=>`} {value}
        </span>
      </div>
    )

    const result = await confirm(content)

    if (!result) {
      newData.fields[index].title = oldValue
      setData(newData)
      return
    }

    setIsLoading(true)

    await updateLoanTermSheetTemp(data.id, newData)
    setData(newData)
    setOrigin(newData)
    setIsLoading(false)

    setEdit(false)
  }

  const onBlurDocumentTitle = async () => {
    if (!data) return
    if (!origin) return
    if (isDuplicated) return
    if ((data.documentTitle || DEFAULT_DOCUMENT_TITLE) === (origin.documentTitle || DEFAULT_DOCUMENT_TITLE)) return

    const title = (data.documentTitle || '').trim() || DEFAULT_DOCUMENT_TITLE
    const newData = cloneDeep(data)
    newData.documentTitle = title
    setData(newData)

    setIsLoading(true)
    await updateLoanTermSheetTemp(newData.id, newData)
    setOrigin(cloneDeep(newData))
    setIsLoading(false)
    setEdit(false)
  }

  const onBlur = async (key: 'headerText' | 'exhibitA' | 'fields', index: number = 0) => {
    if (!data) return
    if (!origin) return
    if (!edit) return
    // Don't auto-save when duplicating - user needs to click "Save as Duplicate" first
    if (isDuplicated) return

    const value: any = ['headerText', 'exhibitA'].includes(key) ? data[key] : data.fields[index].content
    const oldValue: any = ['headerText', 'exhibitA'].includes(key) ? origin[key] : origin.fields[index]?.content

    if (value === oldValue) return

    const { hasError } = validatePlaceholders(value, closingCostPlaceholders)

    if (hasError) {
      const newErrors = cloneDeep(errors)
      newErrors[['headerText', 'exhibitA'].includes(key) ? key : data.fields[index].title] =
        'Invalid placeholders exist'
      setErrors(newErrors)
      setEdit(false)
      toast(`Input valid placeholder`, { type: 'error' })
      return
    }

    const content = (
      <div className="text-gray-400 mb-4 text-[16px]">
        Are you sure to save this content?
        <br />
        <span className="text-gray-800 text-base">
          Title: {['headerText', 'exhibitA'].includes(key) ? termSheetTitles[key] : data?.fields[index].title}
        </span>
      </div>
    )

    const result = await confirm(content)

    if (!result) {
      revertData(key, index)
      return
    }

    setIsLoading(true)

    await updateLoanTermSheetTemp(data.id, data)
    setOrigin(data)
    setIsLoading(false)

    setEdit(false)
  }

  const handleFocus = (event: any, type: 'headerText' | 'exhibitA' | 'fields' | 'title', index: number) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart)
    setFocusField({ type, index })
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart)
  }

  const insertTextAtCursor = (text: string) => {
    const input: any = activeInputRef.current
    if (!input) return

    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length

    const { type, index } = focusField
    if (type === 'title') onChangeTitle(index, newValue)
    else if (type === 'fields') onChangeFields(index, newValue)
    else onChangeHeaderExhibit(type, newValue)
    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  const revertData = (key: 'headerText' | 'exhibitA' | 'fields', index: number = 0) => {
    if (!data) return
    if (!origin) return

    const newData = cloneDeep(data)

    if (['headerText', 'exhibitA'].includes(key)) (newData as any)[key] = origin[key]
    else newData.fields[index].content = origin.fields[index].content

    setData(newData)
  }

  const onChangeOrder = async (val: string, index: number) => {
    if (!data) return
    const newData = cloneDeep(data)

    const nth = Number(val)
    if (nth === index) return

    const newFields = cloneDeep(newData.fields)
    const removedOne = newFields.splice(index, 1)
    newFields.splice(nth, 0, removedOne[0])
    newData.fields = newFields

    // Only update in memory when duplicating
    if (isDuplicated) {
      setData(newData)
      return
    }

    setIsLoading(true)
    await updateLoanTermSheetTemp(newData.id, newData)
    setIsLoading(false)
    setData(newData)
    setOrigin(newData)
  }

  const onAddField = (newData: ILoanTermSheetTemp) => {
    setData(newData)
    setOrigin(newData)
    setAdd(!add)
  }

  const onRemoveField = async (index: number) => {
    if (!data) return

    const content = (
      <div className="text-gray-400 mb-4 text-[16px]">
        Are you sure to remove this field?
        <br />
        <span className="text-gray-800 text-base">Title: {data.fields[index].title}</span>
      </div>
    )

    const result = await confirm(content)

    if (!result) return

    const newData = cloneDeep(data)
    newData.fields.splice(index, 1)

    // Only update in memory when duplicating
    if (isDuplicated) {
      setData(newData)
      return
    }

    setIsLoading(true)
    try {
      await removeLoanTermSheetField(data.id, index)
      setData(newData)
      setOrigin(newData)
    } catch (error) {
      console.log(error)
    }
    setIsLoading(false)
  }

  const onSaveAsDuplicate = async () => {
    if (!data) return

    const newInputs = cloneDeep(inputs)
    let hasError = false

    Object.keys(newInputs).forEach((key) => {
      newInputs[key].error = InputValidate(newInputs[key])
      if (newInputs[key].error) hasError = true
    })

    if (channelError) {
      newInputs.types.error = channelError
      hasError = true
    }

    if ((data.source || 'current') === 'docgen' && !data.docgenTemplateName) {
      toast('Select a Docgen template', { type: 'error' })
      hasError = true
    }

    setInputs(newInputs)
    if (hasError) return

    setIsLoading(true)
    try {
      const reqBody = {
        productType: newInputs.productType.value,
        transactionType: newInputs.transactionType.value,
        types: newInputs.types.value,
        sheetType: newInputs.sheetType.value || DEFAULT_SHEET_TYPE,
        documentTitle: data.documentTitle || DEFAULT_DOCUMENT_TITLE,
        headerText: data.headerText || '',
        exhibitA: data.exhibitA || '',
        hideExhibitA: data.hideExhibitA ?? false,
        fields: cloneDeep(data.fields || []),
        source: data.source || 'current',
        docgenTemplateName: data.source === 'docgen' ? data.docgenTemplateName || null : null,
      }

      await createLoanTermSheetTemp(reqBody)
      toast('Template duplicated successfully.', { type: 'success' })
      onComplete()
      onBack()
    } catch (error: any) {
      console.log(error)
    }
    setIsLoading(false)
  }

  const onCreate = async () => {
    const newInputs = cloneDeep(inputs)
    let hasError = false

    Object.keys(newInputs).forEach((key) => {
      newInputs[key].error = InputValidate(newInputs[key])
      if (newInputs[key].error) hasError = true
    })

    if (channelError) {
      newInputs.types.error = channelError
      hasError = true
    }

    setInputs(newInputs)
    if (hasError) return

    setIsLoading(true)
    try {
      const reqBody = {
        productType: newInputs.productType.value,
        transactionType: newInputs.transactionType.value,
        types: newInputs.types.value,
        sheetType: newInputs.sheetType.value || DEFAULT_SHEET_TYPE,
        documentTitle: DEFAULT_DOCUMENT_TITLE,
        headerText: '',
        exhibitA: '',
        hideExhibitA: false,
        fields: [],
        source: 'current',
        docgenTemplateName: null,
      }

      const created = await createLoanTermSheetTemp(reqBody)
      toast('New template created successfully.', { type: 'success' })
      setData(created)
      setOrigin(created)
    } catch (error: any) {
      console.log(error)
    }
    setIsLoading(false)
  }

  const renderTemp = useMemo(() => {
    if (!data) return <></>

    const displayFields = data.fields.filter((item) => {
      if (!commitmentFeeIsRequired && item.content.includes('{{CommitmentFee}}')) return false
      if (!dueDilligenceIsRequired && item.content.includes('{{DueDilligence}}')) return false
      return true
    })

    const orderOptions = Array.apply(null, Array(displayFields.length)).map((_, index) => index.toString())

    return (
      <>
        <div className="mb-4">
          <RenderInput
            input={{
              inputType: 'text',
              title: 'Document Title',
              value: data.documentTitle || DEFAULT_DOCUMENT_TITLE,
              error: errors.documentTitle,
              className: 'text-center text-2xl font-bold',
            }}
            Key="documentTitle"
            onChange={(_key: string, value: any) => onChangeHeaderExhibit('documentTitle', value)}
            onBlur={onBlurDocumentTitle}
          />
        </div>

        <RenderInput
          input={{
            inputType: 'textarea',
            title: 'Header Text',
            value: data.headerText,
            rows: 15,
            error: errors.headerText,
            additionalElements: (
              <Tooltip message={`<div class="text-sm">Sync</div>`}>
                <div
                  className="p-1 cursor-pointer hover-shadow1 rounded transition-all duration-200"
                  onClick={() => {
                    setModal('sync')
                    setSyncItem({
                      key: 'headerText',
                      index: 0,
                    })
                  }}
                >
                  <ArrowPathIcon className="text-indigo-700 w-4 h-4" />
                </div>
              </Tooltip>
            ),
          }}
          Key={'headerText'}
          onChange={(key: string, value: any) => onChangeHeaderExhibit('headerText', value)}
          onBlur={() => onBlur('headerText')}
          onFocus={(e: any) => handleFocus(e, 'headerText', 0)}
          onKeyUp={handleCursorChange}
          onClick={handleCursorChange}
        />

        <hr className="my-6" />

        <div className="flex justify-end">
          <Button className="transition-all duration-200 w-32" onClick={() => setModal('add')}>
            <div className="flex justify-center items-center gap-2">
              <PlusIcon className="w-3.5 h-3.5" />
              <span>Add</span>
            </div>
          </Button>
        </div>
        <div className="grid grid-cols-9 gap-2">
          {displayFields.map((item, index) => {
            const rows = Math.ceil(item.content.length / 70)
            const additionalElements = (
              <div className="flex gap-2">
                <Tooltip message={`<div class="text-sm">Sync</div>`}>
                  <div
                    className="p-1 cursor-pointer hover-shadow1 rounded transition-all duration-200"
                    onClick={() => {
                      setModal('sync')
                      setSyncItem({
                        key: 'fields',
                        index,
                      })
                    }}
                  >
                    <ArrowPathIcon className="text-indigo-700 w-3.5 h-3.5" />
                  </div>
                </Tooltip>

                <Tooltip message={`<div class="text-sm">Remove</div>`}>
                  <div
                    className="p-1 cursor-pointer hover-shadow1 rounded transition-all duration-200"
                    onClick={() => onRemoveField(index)}
                  >
                    <TrashIcon className="text-red-700 w-3.5 h-3.5" />
                  </div>
                </Tooltip>
              </div>
            )

            return (
              <Fragment key={index}>
                <div className="col-span-2 content-start">
                  <RenderInput
                    input={{
                      inputType: 'text',
                      title: 'Title',
                      type: 'text',
                      value: item.title,
                      error: titleErrors[index],
                    }}
                    Key={'fields'}
                    onChange={(key: string, value: any) => onChangeTitle(index, value)}
                    onBlur={() => onBlurTitle(index)}
                    onFocus={(e: any) => handleFocus(e, 'title', index)}
                    onKeyUp={handleCursorChange}
                    onClick={handleCursorChange}
                  />
                </div>
                <div className="col-span-6 content-start">
                  <div className="mt-[-4px]">
                    <RenderInput
                      input={{
                        inputType: 'textarea',
                        title: 'Content',
                        value: item.content,
                        rows,
                        error: errors[item.title],
                        additionalElements,
                      }}
                      Key={'fields'}
                      onChange={(key: string, value: any) => onChangeFields(index, value)}
                      onBlur={() => onBlur('fields', index)}
                      onFocus={(e: any) => handleFocus(e, 'fields', index)}
                      onKeyUp={handleCursorChange}
                      onClick={handleCursorChange}
                    />
                  </div>
                </div>
                <div className="col-span-1 content-center">
                  <div className="flex justify-center items-center">
                    <select
                      onChange={(e) => onChangeOrder(e.target.value, index)}
                      value={index}
                      className="block rounded py-1.5 px-2 w-20 text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gray-200 peer hover:cursor-pointer"
                    >
                      {orderOptions.map((val) => (
                        <option key={val}>{val}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </Fragment>
            )
          })}
        </div>

        <hr className="my-6" />

        <div className="relative mb-2 min-h-[28px]">
          <div className="text-center text-lg pt-1">Exhibit A</div>
          <div className="absolute right-0 top-0">
            <RenderInput
              input={{
                inputType: 'checkbox',
                title: 'Hide',
                value: !!data.hideExhibitA,
              }}
              Key="hideExhibitA"
              onChange={onChangeHideExhibitA}
            />
          </div>
        </div>
        <RenderInput
          input={{
            inputType: 'textarea',
            title: 'Exhibit A',
            value: data.exhibitA,
            rows: 25,
            error: errors.exhibitA,
            additionalElements: (
              <Tooltip message={`<div class="text-sm">Sync</div>`}>
                <div
                  className="p-1 cursor-pointer hover-shadow1 rounded transition-all duration-200"
                  onClick={() => {
                    setModal('sync')
                    setSyncItem({
                      key: 'exhibitA',
                      index: 0,
                    })
                  }}
                >
                  <ArrowPathIcon className="text-indigo-700 w-3.5 h-3.5" />
                </div>
              </Tooltip>
            ),
          }}
          Key={'exhibitA'}
          onChange={(key: string, value: any) => onChangeHeaderExhibit('exhibitA', value)}
          onBlur={() => onBlur('exhibitA')}
          onFocus={(e: any) => handleFocus(e, 'exhibitA', 0)}
          onKeyUp={handleCursorChange}
          onClick={handleCursorChange}
        />
      </>
    )
  }, [data, errors, add, titleErrors, commitmentFeeIsRequired, dueDilligenceIsRequired, edit, onChangeHideExhibitA])

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Loan Term Sheet / Pre-Approval Template - {isDuplicated ? 'Duplicate' : isNew ? 'Add' : 'Edit'}
      </h2>

      <Button
        link
        onClick={() => {
          onComplete()
          onBack()
        }}
      >
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-2" /> <p>Return to Templates</p>
        </div>
      </Button>

      <div className="flex gap-4 mb-6 mt-3">
        {Object.keys(inputs).map((key, index) => {
          const input = key === 'types' && channelError ? { ...inputs[key], error: channelError } : inputs[key]

          return (
            <div className="w-[350px]" key={index}>
              <RenderInput input={input} Key={key} onChange={onChangeType} onBlur={onBlurType} />
            </div>
          )
        })}
      </div>

      {!!data && (
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="w-[350px]">
            <RenderInput
              input={{
                inputType: 'select',
                title: 'Template Source',
                options: TermSheetTemplateSourceOptions,
                value: templateSource,
                required: true,
              }}
              Key="source"
              onChange={onChangeSource}
            />
          </div>
          {isDocgenSource && (
            <div className="w-[350px]">
              <RenderInput
                input={{
                  inputType: 'select',
                  title: 'Docgen Term Sheet Template',
                  hasDefaultOption: true,
                  options: Object.fromEntries(docgenTemplates.map((doc) => [doc.name, doc.name])),
                  value: data.docgenTemplateName || '',
                  required: true,
                  error:
                    isDocgenSource && !data.docgenTemplateName ? 'Select a Docgen template' : '',
                }}
                Key="docgenTemplateName"
                onChange={onChangeSource}
              />
            </div>
          )}
        </div>
      )}

      {!!data && !isDocgenSource && (
        <>
          <div className="mb-6">
            <div className="flex gap-2 items-center mb-2">
              <span className="ml-3 text-lg font-medium">- Placeholders</span>
              <Tooltip message="Pick Placeholders Up">
                <div
                  className="p-1 hover-shadow1 cursor-pointer rounded transition-all duration-200"
                  onClick={() => setModal('pickUpPlaceholders')}
                >
                  <ListBulletIcon className="w-5 h-5 text-shade-blue" />
                </div>
              </Tooltip>
            </div>
            <div className="p-3 border rounded-md">
              <div className="flex gap-x-1 gap-y-3 flex-wrap justify-between">
                {[...TermSheetPlaceholders, ...closingCostPlaceholders]
                  .filter((item) => {
                    if (!commitmentFeeIsRequired && item === 'CommitmentFee') return false
                    else if (!dueDilligenceIsRequired && item === 'DueDilligence') return false
                    else return true
                  })
                  .sort()
                  .map((item, index) => {
                    return (
                      <pre key={index} className="w-[240px]">
                        <span
                          className="cursor-pointer hover:underline"
                          onClick={() => insertTextAtCursor(`{{${item}}}`)}
                        >
                          {item}
                        </span>
                      </pre>
                    )
                  })}
              </div>
            </div>
          </div>

          {renderTemp}
        </>
      )}

      {!!data && isDocgenSource && (
        <div className="mb-6 p-4 border rounded-md bg-gray-50 text-sm text-gray-700">
          Content will be generated from the selected Docgen Word template. Placeholders in that template are filled
          with loan term sheet values at generation time.
          {!!data.docgenTemplateName && (
            <div className="mt-3">
              <div className="font-medium mb-1">Placeholders in selected template:</div>
              <div className="flex flex-wrap gap-2">
                {(docgenTemplates.find((d) => d.name === data.docgenTemplateName)?.placeholders || []).map((p) => (
                  <span key={p} className="px-2 py-0.5 bg-white border rounded text-xs">
                    {`{{${p}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {modal === 'sync' && !!data && (
        <SyncTermSheetField item={syncItem as any} data={data} onClose={() => setModal('')} />
      )}
      {modal === 'add' && !!data && (
        <AddTermSheetField
          data={data}
          additionalPlaceholders={closingCostPlaceholders}
          onAddField={onAddField}
          onClose={() => setModal('')}
        />
      )}
      {modal === 'pickUpPlaceholders' && !!globalSetting && (
        <PickPlaceholdersUp
          globalSetting={globalSetting}
          onClose={() => setModal('')}
          setGlobalSetting={setGlobalSetting}
          additionalPlaceholders={closingCostPlaceholders}
        />
      )}

      <SaveChanges
        show={(isNew && !data) || (isDuplicated && !!data)}
        label={isNew && !data ? 'Create' : 'Save Changes'}
        onSave={isNew && !data ? onCreate : onSaveAsDuplicate}
      />
    </div>
  )
}
